import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Anchor, Plus, Trash2, Ship, MapPin } from "lucide-react";

const emptyBoat = { name: "", size: "8-12m", boat_type: "motor", year: "", home_port: "", home_province: "", notes: "" };

export default function MyBoats() {
  const [boats, setBoats] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyBoat);

  const load = () => api.get("/boats").then(r => setBoats(r.data)).catch(e => toast.error(apiErr(e)));
  useEffect(() => {
    load();
    api.get("/geo/provinces").then(r => setProvinces(r.data)).catch(() => {});
  }, []);

  const currentProv = provinces.find(p => p.name === f.home_province);

  const save = async () => {
    if (!f.name || !f.size) { toast.error("Nombre y eslora obligatorios"); return; }
    try {
      await api.post("/boats", { ...f, year: f.year ? parseInt(f.year) : null });
      toast.success("Barco guardado");
      setOpen(false); setF(emptyBoat); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar este barco?")) return;
    await api.delete(`/boats/${id}`);
    toast.success("Barco eliminado"); load();
  };

  return (
    <div className="min-h-screen hub-cream">
      <Navbar/>
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <div className="hub-pill hub-sand text-[#0B1D3A] mb-3"><Anchor size={12}/> Mi flota</div>
            <h1 className="font-serif text-5xl text-[#0B1D3A]">Mis barcos</h1>
            <p className="text-[#5C6B7D] mt-2">Guarda tus barcos y elige uno al crear cada solicitud.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="hub-btn-primary gap-2" data-testid="add-boat-btn"><Plus size={16}/> Añadir barco</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif text-3xl">Nuevo barco</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nombre</Label><Input value={f.name} onChange={e=>setF({...f, name:e.target.value})} data-testid="boat-name"/></div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label>Eslora</Label>
                    <Select value={f.size} onValueChange={v=>setF({...f, size:v})}>
                      <SelectTrigger data-testid="boat-size"><SelectValue/></SelectTrigger>
                      <SelectContent>{["< 8m","8-12m","12-18m","> 18m"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Tipo</Label>
                    <Select value={f.boat_type} onValueChange={v=>setF({...f, boat_type:v})}>
                      <SelectTrigger data-testid="boat-type"><SelectValue/></SelectTrigger>
                      <SelectContent>{["motor","velero","catamarán","semirrígida","pesquero"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Año</Label><Input type="number" value={f.year} onChange={e=>setF({...f, year:e.target.value})} data-testid="boat-year"/></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Provincia</Label>
                    <Select value={f.home_province} onValueChange={v => setF({ ...f, home_province: v, home_port: "" })}>
                      <SelectTrigger data-testid="boat-province"><SelectValue placeholder="Elige"/></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {provinces.map(p => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Puerto base</Label>
                    <Select value={f.home_port} onValueChange={v => setF({ ...f, home_port: v })} disabled={!currentProv}>
                      <SelectTrigger data-testid="boat-port"><SelectValue placeholder={currentProv ? "Elige puerto" : "Elige provincia"}/></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {currentProv?.ports.map(p => {
                          const name = typeof p === "string" ? p : p.name;
                          return <SelectItem key={name} value={name}>{name}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-[#5C6B7D] italic flex items-center gap-1"><MapPin size={11}/> Los profesionales de este puerto verán tus solicitudes al instante.</p>
                <div><Label>Notas</Label><Textarea value={f.notes} onChange={e=>setF({...f, notes:e.target.value})} data-testid="boat-notes"/></div>
                <Button onClick={save} className="hub-btn-primary w-full" data-testid="boat-save">Guardar</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {boats.length === 0 && <div className="hub-card p-10 text-center text-[#5C6B7D] col-span-2">Aún no has añadido barcos.</div>}
          {boats.map(b => (
            <div key={b.id} className="hub-card p-6" data-testid={`boat-${b.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="hub-pill hub-sand text-[#0B1D3A]"><Ship size={11}/> {b.boat_type}</div>
                  <h3 className="font-serif text-2xl mt-2">{b.name}</h3>
                  <div className="text-sm text-[#5C6B7D] mt-1">{b.size}{b.year ? ` · ${b.year}` : ""}{b.home_port ? ` · ${b.home_port}` : ""}</div>
                  {b.notes && <p className="text-sm text-[#0B1D3A]/80 mt-3">{b.notes}</p>}
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(b.id)} className="text-red-500" data-testid={`del-boat-${b.id}`}><Trash2 size={14}/></Button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
